/**
 * @module lazyLoader
 * @description A singleton class to handle LazyLoad instance that loads images/iframes while they reach a threshold
 * value relatively to viewport bounds. Positive threshold values load sooner, negative values load later.
 *
 * There is an extra feature added to start animations for custom elements that reach the viewport — `data-lazyload`.
 *
 * @see https://github.com/verlok/lazyload
 * @see components/lazyLoader/lazyLoader.css
 */
import LazyLoad from 'vanilla-lazyload/dist/esm/lazyload.js';
import {holdLoopsOffScreen} from '../modules/motion/onScreen.js';
const ELEMENTS_SELECTOR = 'img[data-src],[data-lazyload]';
const PLYR_SPRITE_URL = '/svg/plyr-sprite.svg';

/*
 * Elements that already carry a listener. `initVideoWithinContainer(document)` runs again on
 * every `htmx:afterSwap`, and a second overlay listener means one click calls play() twice,
 * which makes Firefox flap between play and pause. WeakSets, not data attributes: a carousel
 * clones its items with cloneNode(), which copies attributes but not listeners, so a clone
 * must bind again.
 */
/** @type {WeakSet<Element>} */
const boundOverlays = new WeakSet();
/** @type {WeakSet<HTMLVideoElement>} */
const exclusivePlaybackBound = new WeakSet();
/** @type {WeakSet<Element>} */
const heldPosters = new WeakSet();

const LazyLoadInstance = new LazyLoad({
    elements_selector: ELEMENTS_SELECTOR,
    thresholds: '10%', // % of the viewport height
    unobserve_entered: true,
    callback_enter(element) {
        // Add a class name to element when it starts loading. Such allows, for example, to show a placeholder image.
        element.classList.add('lazy-loaded');

        /*
         * Check for `data-lazyload` attribute on element first. If exists then remove it after a delay specified in it.
         * Using this attribute may be useful when the element is not an image/video/iframe, but any custom element
         * that has to be shown after a delay after reaching the viewport. For example, embedded SVG with animations.
         */
        const lazyloadDelay = parseInt(element.getAttribute('data-lazyload') || 0);
        if (lazyloadDelay) {
            setTimeout(() => element.removeAttribute('data-lazyload'), lazyloadDelay);
        }
    },
    callback_loaded(element) {
        // Find and reveal any elements that should be hidden until the image loads
        // Look for elements with class 'js-hide-until-loaded' within the same parent container
        const container = element.closest('[data-lazy-container]') || element.parentElement;
        if (container) {
            container.querySelectorAll('.js-hide-until-loaded').forEach((hiddenElement) => {
                hiddenElement.classList.remove('js-hide-until-loaded');
                hiddenElement.classList.add('opacity-100');
            });

            // Hide skeleton loaders once content is loaded
            container.querySelectorAll('.js-skeleton-loader').forEach((skeletonElement) => {
                skeletonElement.classList.add('hidden');
            });
        }
    },
});

LazyLoadInstance.updateWithinContainer = (element) => {
    const newElements = element.querySelectorAll(ELEMENTS_SELECTOR);
    if (LazyLoadInstance._observer) {
        newElements.forEach((element_) => LazyLoadInstance._observer.observe(element_));
    }
};

// Use an IntersectionObserver to load <video> elements as the verlok/lazyload
// library will trigger video src to be automatically downloaded on Safari.
const videoIo = new IntersectionObserver((entries, observer) => {
    entries.forEach(function (entry) {
        const {target} = entry;
        if (entry.intersectionRatio > 0) {
            lazyInitVideo(target);
            observer.unobserve(target);
        }
    });
});

/**
 * Adds special query params to YouTube's video URL.
 * See https://developers.google.com/youtube/player_parameters
 *
 * @param {string} url
 * @returns {string}
 */
function appendYoutubeQueryParameters(url) {
    const parametersSeparator = url.includes('?') ? '&' : '?';
    const origin = `${location.protocol}//${location.hostname}`;

    return `${url}${parametersSeparator}origin=${origin}&iv_load_policy=3&modestbranding=1&playsinline=1&enablejsapi=1&showinfo=0&rel=0`;
}

/**
 * Adds special query params to Vimeos's video URL.
 * See https://developers.google.com/youtube/player_parameters
 *
 * @param {string} url
 * @returns {string}
 */
function appendVimeoQueryParameters(url) {
    const parametersSeparator = url.includes('?') ? '&' : '?';
    const origin = `${location.protocol}//${location.hostname}`;

    return `${url}${parametersSeparator}origin=${origin}&loop=false&byline=false&portrait=false&title=false&speed=true&transparent=0&gesture=media`;
}

/**
 * Hydrate deferred <source data-src="..."> tags only when video enters viewport.
 * @param {HTMLVideoElement} element
 */
function hydrateVideoSources(element) {
    if (!(element instanceof HTMLVideoElement)) {
        return;
    }

    const sourceElements = element.querySelectorAll('source[data-src]');

    if (sourceElements.length === 0) {
        return;
    }

    sourceElements.forEach((source) => {
        const sourceUrl = source.dataset.src;
        if (!sourceUrl) {
            return;
        }

        source.src = sourceUrl;
        source.removeAttribute('data-src');
    });

    element.load();
}

/**
 * Init video player for DOM element
 * @param {HTMLVideoElement} element
 * @param {object} options additional Plyr options
 */
function lazyInitVideo(element, options = {}) {
    // Hydrate a deferred poster (data-poster → poster) so duplicated mobile/desktop
    // <video> elements don't fetch their poster images on initial page load.
    if (element.dataset.poster) {
        element.poster = element.dataset.poster;
        delete element.dataset.poster;
    }

    if (element.classList.contains('video-gif')) {
        if (element.dataset.src) {
            element.src = element.dataset.src;
        }
        return;
    }

    // Copy data-src → src on <source> elements so the browser starts fetching video data
    hydrateVideoSources(element);

    // Videos that opt out of Plyr: leave them as native HTML5 video
    // e.g.: short testimonials videos section.
    if (element.classList.contains('no-plyr')) {
        // When opted in, pause all other exclusive-playback videos when this one starts.
        if (element.hasAttribute('data-exclusive-playback') && !exclusivePlaybackBound.has(element)) {
            exclusivePlaybackBound.add(element);
            element.addEventListener('play', (event) => {
                document.querySelectorAll('video[data-exclusive-playback]').forEach((other) => {
                    if (other !== event.currentTarget && !other.paused) {
                        other.pause();
                    }
                });
            });
        }

        // Play video immediately if autoplay is enabled
        if (options.autoplay) {
            element.play();
        }
        return;
    }

    import('./video.js').then(({initVideo}) => {
        initVideo(element, {...options, iconUrl: PLYR_SPRITE_URL});
    });
}

/**
 * Handle mouse/touch event on iframe with video
 * @param {Event} event Mouse event object
 */
function handleIframeMouseOver(event) {
    const element = event.target;
    // initialize video player only once
    element.removeEventListener('mouseover', handleIframeMouseOver);
    // start downloading external resources
    element.src = element.dataset.src;
    // Fix aspect ratio for Vimeo videos using extra parameter
    lazyInitVideo(element.parentNode, {ratio: '16:9'});
}

/**
 * Setup various video players in container
 * @param {HTMLElement|Document} container
 */
function initVideoWithinContainer(container) {
    container.querySelectorAll('.js-video-loading-overlay').forEach((poster) => {
        if (heldPosters.has(poster)) {
            return;
        }
        heldPosters.add(poster);
        // The play-button pulse loops forever. A draggable carousel moves its cards on the compositor, so watch its box.
        holdLoopsOffScreen(poster, poster.closest('[data-draggable-carousel]') ?? poster);
    });

    container.querySelectorAll('video').forEach((video) => {
        // Hide browser controls immediately so they don't flash before Plyr takes over.
        // Skip no-plyr videos (they keep whatever the HTML markup specifies).
        if (!video.classList.contains('no-plyr')) {
            video.controls = false;
        }

        const hasDeferredSources = video.querySelector('source[data-src]');
        const overlay = hasDeferredSources
            ? video.parentElement?.querySelector('.js-video-loading-overlay')
            : null;

        if (overlay) {
            if (boundOverlays.has(overlay)) {
                return;
            }
            boundOverlays.add(overlay);

            // Click-to-play: defer everything until user clicks
            overlay.style.cursor = 'pointer';

            const activateOverlay = (event) => {
                if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') {
                    return;
                }

                event.preventDefault();
                overlay.removeEventListener('click', activateOverlay);
                overlay.removeEventListener('keydown', activateOverlay);

                // For non-Plyr videos, remove overlay immediately since there's no Plyr 'ready' event to do it.
                // For Plyr videos, the overlay is removed in video.js on the player 'ready' event.
                if (video.classList.contains('no-plyr')) {
                    overlay.remove();
                }

                lazyInitVideo(video, {autoplay: true});
            };

            overlay.addEventListener('click', activateOverlay);
            overlay.addEventListener('keydown', activateOverlay);
        } else {
            // Standard: initialize on viewport intersection
            videoIo.observe(video);
        }
    });

    container.querySelectorAll('iframe[data-src]').forEach((iframe) => {
        const element = iframe;
        const url = element.dataset.src;
        const isYoutube = url && url.includes('youtube');
        const isVimeo = url && url.includes('vimeo');
        const isVideoEmbedded = isYoutube || isVimeo;
        // display poster using srcdoc attribute
        if (isYoutube) {
            element.style.backgroundImage = `url(https://img.youtube.com/vi/${url.split('/').pop()}/hqdefault.jpg)`;
            element.style.backgroundSize = 'cover';
            element.style.backgroundPosition = 'center';
            element.dataset.src = appendYoutubeQueryParameters(url);
        } else if (isVimeo) {
            // vimeo doesn't provide an easy access to poster URL :(
            // https://stackoverflow.com/questions/1361149/get-img-thumbnails-from-vimeo
            element.dataset.src = appendVimeoQueryParameters(url);
        }
        if (isVideoEmbedded) {
            const videoPosterCssPath = document.getElementById('videoPosterCssPath').href;
            element.srcdoc = `
            <link rel=stylesheet href="${videoPosterCssPath}">
            <button>
                <svg aria-hidden=true>
                    <g viewBox="0 0 18 18"><path d="M15.562 8.1L3.87.225c-.818-.562-1.87 0-1.87.9v15.75c0 .9 1.052 1.462 1.87.9L15.563 9.9c.584-.45.584-1.35 0-1.8z"/></g>
                </svg>
            </button>
            `;
            element.addEventListener('mouseover', handleIframeMouseOver);
        }
    });
}

initVideoWithinContainer(document);

document.addEventListener(
    'lazy:initContent',
    (event) => {
        LazyLoadInstance.updateWithinContainer(event.target);
    },
    true
);

document.addEventListener(
    'lazy:initVideo',
    (event) => {
        initVideoWithinContainer(event.target);
    },
    true
);

// Re-observe lazy-loaded images and videos after HTMX swaps new content into the DOM.
// This is especially important for load-more pagination that loads cards (e.g course and masterclass cards) with data-src images.
document.addEventListener('htmx:afterSwap', () => {
    document.dispatchEvent(new CustomEvent('lazy:initContent'));
    document.dispatchEvent(new CustomEvent('lazy:initVideo'));
});
